import axiosInstance from "@/lib/axios";
import type { paginatedResponse } from "@/types/api/paginated.api";
import type { user } from "@/types/api/user.api";
import type { dashboard, simulation } from "@/features/dashboard/types.api";
import type { facilitatorPanelData } from "@/features/facilitatorPanel/types.props";

export const getSimulations = async (
    studentId?: string | null,
    institutionId?: string | null,
): Promise<simulation[]> => {
    let url = "/reports/student/simulations/";

    if (institutionId) {
        url = `/reports/coordinator/simulations/?institution_id=${institutionId}`;
    } else if (studentId) {
        url = `/reports/student/simulations/?student_id=${studentId}`;
    }
    return axiosInstance.get(url).then((res) => res.data);
};

export const getDashboardData = async (
    simulationId: string | number,
    studentId?: string | null,
    institutionId?: string | null,
): Promise<dashboard> => {
    let url = `/reports/student/simulations/${simulationId}/dashboard/`;

    if (institutionId) {
        url = `/reports/coordinator/simulations/${simulationId}/dashboard/?institution_id=${institutionId}`;
    } else if (studentId) {
        url = `/reports/student/simulations/${simulationId}/dashboard/?student_id=${studentId}`;
    }

    return axiosInstance.get(url).then((res) => res.data);
};

export const updateRecommendations = async (
    studentAnswersId: number,
    recommendations: string[],
): Promise<{ recommendations: string[] }> => {
    return axiosInstance
        .patch(`/reports/manage/answers/${studentAnswersId}/recommendations/`, {
            recommendations,
        })
        .then((res) => res.data);
};

export const exportStudentReport = async (
    studentId: string | number,
    simulationId: string | number,
): Promise<Blob> => {
    const { data } = await axiosInstance.get<Blob>(
        `/reports/export/student/${studentId}/simulation/${simulationId}/`,
        { responseType: "blob" },
    );
    return data;
};

export const exportInstitutionReport = async (
    institutionId: string | number,
    simulationId: string | number,
): Promise<Blob> => {
    const { data } = await axiosInstance.get<Blob>(
        `/reports/export/institution/${institutionId}/simulation/${simulationId}/`,
        { responseType: "blob" },
    );
    return data;
};

export const getInstitutionStudents = async (
    institutionId: string | number,
    filters: { page: number; search?: string },
): Promise<paginatedResponse<user>> => {
    if (!institutionId) {
        throw new Error('Institution ID is required');
    }
    const { data } = await axiosInstance.get<paginatedResponse<user>>(
        `/reports/coordinator/institutions/${institutionId}/students/`,
        { params: filters },
    );
    return data;
};

export const updateStudentRecommendations = async (
    studentAnswersId: number | string,
    recommendations: string[],
): Promise<{ recommendations: string[] }> => {
    const { data } = await axiosInstance.patch<{ recommendations: string[] }>(
        `/reports/manage/answers/${studentAnswersId}/recommendations/`,
        { recommendations },
    );
    return data;
};

export const getFacilitatorPanelData =
    async (): Promise<facilitatorPanelData> => {
        const { data } = await axiosInstance.get<facilitatorPanelData>(
            `/reports/panel/facilitator/`,
        );
        return data;
    };
